import { useEffect, useState } from 'react';
import { Modal, TextInput, Stack, Group, Button } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { partnersApi } from '../../../services/api/partners';
import { RichTextEditor } from '../../../components/common/RichTextEditor/RichTextEditor';
import type {
  PartnerContactWithCohortCount,
  CreatePartnerContactRequest,
  UpdatePartnerContactRequest,
} from '../../../types';

interface PartnerContactModalProps {
  opened: boolean;
  onClose: () => void;
  partnerId: string;
  contact: PartnerContactWithCohortCount | null;
  onSaved: () => void;
}

export function PartnerContactModal({
  opened,
  onClose,
  partnerId,
  contact,
  onSaved,
}: PartnerContactModalProps) {
  const [saving, setSaving] = useState(false);
  const isEditing = !!contact;

  const form = useForm<CreatePartnerContactRequest>({
    initialValues: {
      first_name: '',
      last_name: '',
      email: '',
      job_title: '',
      internal_notes: '',
    },
    validate: {
      first_name: value => (!value?.trim() ? 'First name is required' : null),
      last_name: value => (!value?.trim() ? 'Last name is required' : null),
      email: value => {
        if (!value?.trim()) return 'Email is required';
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) return 'Invalid email format';
        return null;
      },
    },
  });

  // Reset form when modal opens/closes or contact changes
  useEffect(() => {
    if (opened) {
      if (contact) {
        form.setValues({
          first_name: contact.first_name,
          last_name: contact.last_name,
          email: contact.email,
          job_title: contact.job_title || '',
          internal_notes: contact.internal_notes || '',
        });
      } else {
        form.reset();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, contact]);

  const handleSubmit = async (values: CreatePartnerContactRequest) => {
    try {
      setSaving(true);

      if (isEditing && contact) {
        const updateData: UpdatePartnerContactRequest = {};
        if (values.first_name !== contact.first_name) updateData.first_name = values.first_name;
        if (values.last_name !== contact.last_name) updateData.last_name = values.last_name;
        if (values.email !== contact.email) updateData.email = values.email;
        if ((values.job_title || '') !== (contact.job_title || ''))
          updateData.job_title = values.job_title || undefined;
        if ((values.internal_notes || '') !== (contact.internal_notes || ''))
          updateData.internal_notes = values.internal_notes || undefined;

        await partnersApi.updateContact(partnerId, contact.id, updateData);
        notifications.show({
          title: 'Success',
          message: 'Contact updated successfully',
          color: 'green',
        });
      } else {
        await partnersApi.createContact(partnerId, values);
        notifications.show({
          title: 'Success',
          message: 'Contact created successfully',
          color: 'green',
        });
      }

      onSaved();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to save contact',
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={isEditing ? 'Edit Contact' : 'Add Contact'}
      size="md"
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <Group grow>
            <TextInput
              label="First Name"
              placeholder="e.g., John"
              required
              {...form.getInputProps('first_name')}
            />
            <TextInput
              label="Last Name"
              placeholder="e.g., Smith"
              required
              {...form.getInputProps('last_name')}
            />
          </Group>

          <TextInput
            label="Email"
            placeholder="e.g., john.smith@example.com"
            required
            {...form.getInputProps('email')}
          />

          <TextInput
            label="Job Title"
            placeholder="e.g., Program Manager"
            {...form.getInputProps('job_title')}
          />

          <RichTextEditor
            label="Internal Medley Notes"
            placeholder="Add notes about this contact (only visible to Medley staff)..."
            value={form.values.internal_notes || ''}
            onChange={(html) => form.setFieldValue('internal_notes', html)}
            minHeight={150}
          />

          <Group justify="flex-end" className="modal-footer">
            <Button variant="default" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" loading={saving}>
              {isEditing ? 'Save Changes' : 'Add Contact'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

export default PartnerContactModal;
